/**
 * Devices store
 * @module store/devices
 */

import { createStore, createHook } from '@thermon-platforms/react-sweet-state';
import api, { parseErrors } from 'util/api';


/**
 * Recursively compile a shallow map of device data
 * @function compileDeviceMap
 * @memberof module:store/devices
 * @param {array} devices Modeled device heirarchy
 * @param {map} devicesMap (optional)
 * @param {array} path Heirarchy of the current device parent nodes
 * @example
 * // This allows us to easily grab a device model by id:
 * devicesStore.shallowDevices.get(id);
 */
export function compileDeviceMap(devices, devicesMap=new Map(), path=[]) {
  devices.forEach(device => {
    devicesMap.set(device.id, {
      model: device,
      path
    });
    if (device.children?.length) {
      compileDeviceMap(device.children, devicesMap, [...path, device]);
    }
  })
  return devicesMap;
}

// define store initial state
const initialState = {
  devices: [],
  loading: false,
  error: undefined,
  shallowDevices: new Map(),
  updated: undefined
};
// define the actions that mutate the state
export const actions = {
  // NOTE - Once we can actually save models to the API
  // there might be a better way to handle this
  forceUpdate: () => ({setState}) => {
    setState({updated: Date.now()});
  },
  query: () => async ({ getState, setState }) => {
    if (getState().loading) return;
    setState({ loading: true });

    try {
      // Fetch our data
      const res = await api('/devices');
      // and unwrap it
      const devices = await res.json();

      // Use our devices models to update our state including creating our
      // shallow map for easy reference
      setState({
        devices,
        loading: false,
        shallowDevices: compileDeviceMap(devices, new Map()),
        updated: Date.now(),
        error: undefined
      });
    } catch (error) {
      // If that fails, update our state with the error
      setState({
        error,
        loading: false,
        updated: Date.now()
      });
    }
  },
  save: (device) => async ({ dispatch }) => {
    const method = device.id ? 'PUT' : 'POST';
    const path = device.id ? `/devices/${device.id}` : '/devices';
    const res = await api(path, {
      method,
      body: JSON.stringify(device)
    });

    if (!res.ok) {
      throw new Error(parseErrors(await res.json()));
    }

    dispatch(actions.query());

    return await res.json();
  },
  update: (device) => async ({ dispatch }) => {
    const res = await api(`/devices/${device.id}`, {
      method: 'PATCH',
      body: JSON.stringify(device)
    });

    if (!res.ok) {
      throw new Error(parseErrors(await res.json()));
    }

    dispatch(actions.query());

    return await res.json();
  },
  remove: (device) => async ({ dispatch }) => {
    const res = await api(
      `/devices/${device.id}?deviceType=${device.type}`,
      {method: 'DELETE'}
    );

    if (!res.ok) {
      throw new Error(parseErrors(await res.json()));
    }

    dispatch(actions.query());
  },
  addChild: (child, parent) => async ({ dispatch }) => {
    const path = `/devices/${parent.id}/children`;
    const res = await api(path, {
      method: 'POST',
      body: JSON.stringify(child)
    });

    if (!res.ok) {
      throw new Error(parseErrors(await res.json()));
    }

    dispatch(actions.query());

    return await res.json();
  },
  fetchCreationSettings: (parentId, type) => async() => {
    try {
      const res = await api(`/devices/creationSettings?parentId=${parentId}&deviceType=${type}`);
      const data = await res.json();
      return data;
    } catch (e) {
      console.error(e);
    }
  },
  fetchDetail: (id) => async() => {
    try {
      const res = await api(`/devices/${id}`);
      const data = await res.json();
      return data;
    } catch (e) {
      console.error(e);
    }
  },
  fetchStatus: (id) => async() => {
    try {
      const res = await api(`/devices/${id}/status`);
      const data = await res.json();
      return data;
    } catch (e) {
      console.error(e);
    }
  },
  refresh: (id) => async() => {
    try {
      const res = await api(`/devices/${id}/refresh`, {method: 'PATCH'});
    } catch (e) {
      console.error(e);
    }
  },
}

// create a store type
const Store = createStore({ initialState, actions });

/**
 * Expose hooks for store
 * @function useStore
 * @memberof module:store/devices
 * @example
 * const [devicesStore, devicesStoreActions] = useDevicesStore();
 */
const useStore = createHook(Store);

export default useStore;
